/**
 * Fibonacci levels editor — floating, non-modal, draggable popup.
 *
 * Edit model mirrors Chart Properties:
 *   - changes apply LIVE to the redux slice so every chart re-
 *     renders as the user fiddles (color tweaks, opacity slider,
 *     enable/disable, add/delete a level)
 *   - opening the dialog snapshots `state.fibLevels.levels` to a
 *     rollback ref; Cancel / × / drag-aware backdrop restores it
 *   - Save POSTs the current levels to the server (replaces the
 *     previous draft-based save flow)
 *
 * The chart is fully interactive while the dialog is open — no
 * dimming backdrop, no click-outside-to-close. Drag the header
 * to reposition.
 */
import { useEffect, useRef, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
  useActiveTabId,
} from "@/state/hooks";
import {
  DEFAULT_FIB_LINE_STYLE,
  DEFAULT_FIB_LINE_WIDTH,
  DEFAULT_LEVELS,
  saveFibLevels,
  setFibDiagonal,
  setFibLabelSide,
  setFibLevels,
  setFibLineStyle,
  setFibLineWidth,
  setFibShowDiagonal,
} from "@/state/slices/fibLevelsSlice";
import type { FibDiagonalConfig } from "@/api/types";
import {
  setTabFibLevels,
  setTabFibLineStyle,
  setTabFibLineWidth,
  type LineStyleName,
} from "@/state/slices/uiSlice";
import {
  LineStyleDialog,
  type LineStyleConfig,
} from "@/components/LineStyleDialog/LineStyleDialog";
import { ColorPicker } from "@/components/ColorPicker/ColorPicker";
import { SettingsPanel } from "@/tools/_shared/SettingsPanel";
import { useScopedCascade } from "@/tools/_shared/useScopedCascade";
import type { FibLevel } from "@/api/types";
import styles from "@/tools/_shared/panel.module.css";

// Two scopes: "Workspace" = per-tab override (saved with the
// workspace so each chart can have its own fib levels);
// "User defaults" = server-persisted user-wide fallback (the
// existing /api/fib_levels).
type Scope = "workspace" | "user";

const SCOPE_LABELS: Record<Scope, string> = {
  workspace: "Workspace",
  user: "User defaults",
};

// The scoped portion of fib that runs on the shared 2-scope engine: the level
// list + the shared line width/style (colours stay per-level in the grid).
type FibScopeValue = {
  levels: FibLevel[];
  lineWidth: number;
  lineStyle: LineStyleName;
};

interface Props {
  open: boolean;
  onClose: () => void;
}

const DEFAULT_NEW_RATIO = 0.5;
const DEFAULT_NEW_COLOR = "#787B86";

function ratioToPercentInput(r: number): string {
  // Show with up to 3 decimals, no trailing zeros: 0.236 → "23.6", 0.5 → "50"
  return (r * 100).toLocaleString("en-US", {
    useGrouping: false,
    maximumFractionDigits: 4,
  });
}

/** `78.6 / 100` is `0.7859999999999999` in IEEE 754; snap to 4
 *  decimals so the stored value matches what the user typed. */
function percentToRatio(pct: number): number {
  return Math.round(pct * 100) / 10000;
}

export function FibLevelsDialog({ open, onClose }: Props) {
  const dispatch = useAppDispatch();
  const userLevels = useAppSelector((s) => s.fibLevels.levels);
  const userLineWidth = useAppSelector((s) => s.fibLevels.lineWidth);
  const userLineStyle = useAppSelector((s) => s.fibLevels.lineStyle);
  // Diagonal appearance + default visibility + default label side are
  // GLOBAL (user-wide), independent of the workspace/user scope toggle
  // (which only governs the level list). Edited straight on the
  // fibLevels slice; persisted via the same Save (PUT /api/fib_levels).
  const userDiagonal = useAppSelector((s) => s.fibLevels.diagonal);
  const userShowDiagonal = useAppSelector((s) => s.fibLevels.showDiagonal);
  const userLabelSide = useAppSelector((s) => s.fibLevels.labelSide);
  const saving = useAppSelector((s) => s.fibLevels.saving);
  const error = useAppSelector((s) => s.fibLevels.error);

  const activeTabId = useActiveTabId();
  const activeSymbol = useAppSelector(
    (s) => s.ui.byTab[activeTabId]?.symbol ?? "",
  );
  const tabLevelsOverride = useAppSelector(
    (s) => s.ui.byTab[activeTabId]?.fibLevels ?? null,
  );
  const tabLineWidth = useAppSelector(
    (s) => s.ui.byTab[activeTabId]?.fibLineWidth ?? null,
  );
  const tabLineStyle = useAppSelector(
    (s) => s.ui.byTab[activeTabId]?.fibLineStyle ?? null,
  );

  // The scoped part of fib (levels + shared line width/style) runs on the same
  // shared 2-scope engine as Indicators / Chart Properties. The "override
  // exists" signal is the per-tab LEVELS slot; width/style ride along with it.
  // (The GLOBAL diagonal / label-side settings + the always-save and all-tab
  // rollback stay bespoke below — they don't fit the generic single-value
  // engine.)
  const fibTabValue: FibScopeValue | null =
    tabLevelsOverride !== null
      ? {
          levels: tabLevelsOverride,
          lineWidth: tabLineWidth ?? userLineWidth ?? DEFAULT_FIB_LINE_WIDTH,
          lineStyle: tabLineStyle ?? userLineStyle ?? DEFAULT_FIB_LINE_STYLE,
        }
      : null;
  const fibUserValue: FibScopeValue = {
    levels: userLevels,
    lineWidth: userLineWidth ?? DEFAULT_FIB_LINE_WIDTH,
    lineStyle: userLineStyle ?? DEFAULT_FIB_LINE_STYLE,
  };
  const hook = useScopedCascade<FibScopeValue>(
    {
      tabValue: fibTabValue,
      userValue: fibUserValue,
      defaultValue: {
        levels: DEFAULT_LEVELS,
        lineWidth: DEFAULT_FIB_LINE_WIDTH,
        lineStyle: DEFAULT_FIB_LINE_STYLE,
      },
      writeTab: (v) => {
        dispatch(
          setTabFibLevels({ tabId: activeTabId, fibLevels: v?.levels ?? null }),
        );
        dispatch(
          setTabFibLineWidth({
            tabId: activeTabId,
            lineWidth: v?.lineWidth ?? null,
          }),
        );
        dispatch(
          setTabFibLineStyle({
            tabId: activeTabId,
            lineStyle: v?.lineStyle ?? null,
          }),
        );
      },
      writeUser: (v) => {
        if (!v) return;
        dispatch(setFibLevels(v.levels));
        dispatch(setFibLineWidth(v.lineWidth));
        dispatch(setFibLineStyle(v.lineStyle));
      },
      // fib persists via its own always-save handleSave (it also carries the
      // global diagonal/label fields); the generic save path is unused here.
      persistUser: () => {},
      clone: (v) => ({
        levels: v.levels.map((l) => ({ ...l })),
        lineWidth: v.lineWidth,
        lineStyle: v.lineStyle,
      }),
      saving,
      workspaceLabel: `Workspace${activeSymbol ? ` (${activeSymbol})` : ""}`,
    },
    open,
  );
  const scope = hook.scope;

  // Derived view of the scoped values for the existing JSX (unchanged below).
  const editingLevels: FibLevel[] | null = hook.isInheriting
    ? null
    : hook.current.levels;
  const editingLineWidth: number = hook.current.lineWidth;
  const editingLineStyle: LineStyleName = hook.current.lineStyle;

  // Sub-popup open state for the shared line-style editor.
  const [lineStyleDialogOpen, setLineStyleDialogOpen] = useState(false);
  // …and for the diagonal-line editor (its own color/width/style).
  const [diagonalDialogOpen, setDiagonalDialogOpen] = useState(false);

  /** Next scope down — Workspace (per-tab) inherits from User;
   *  User has no inheritance source. */
  const inheritFromLabel: string = SCOPE_LABELS.user;

  // New-row form is the one piece of TRULY local state — the new
  // level only enters `levels` on "+ Add", so it doesn't need to
  // round-trip through redux on every keystroke.
  const [newRatioStr, setNewRatioStr] = useState(
    ratioToPercentInput(DEFAULT_NEW_RATIO),
  );
  const [newColor, setNewColor] = useState(DEFAULT_NEW_COLOR);
  const [newOpacity, setNewOpacity] = useState(1);

  // Rollback snapshot — captured once on each open. Captures
  // BOTH scopes (User + every tab's override) plus the shared
  // line-style fields so Cancel can restore them.
  const rollbackRef = useRef<{
    user: FibLevel[];
    userLineWidth: number;
    userLineStyle: LineStyleName;
    userDiagonal: FibDiagonalConfig;
    userShowDiagonal: boolean;
    userLabelSide: "left" | "right";
    tabs: Record<string, FibLevel[] | null>;
    tabLineWidths: Record<string, number | null>;
    tabLineStyles: Record<string, LineStyleName | null>;
  } | null>(null);
  const allByTab = useAppSelector((s) => s.ui.byTab);
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      const tabSnap: Record<string, FibLevel[] | null> = {};
      const tabWidthSnap: Record<string, number | null> = {};
      const tabStyleSnap: Record<string, LineStyleName | null> = {};
      for (const [tid, tabState] of Object.entries(allByTab)) {
        tabSnap[tid] = tabState.fibLevels ?? null;
        tabWidthSnap[tid] = tabState.fibLineWidth ?? null;
        tabStyleSnap[tid] = tabState.fibLineStyle ?? null;
      }
      rollbackRef.current = {
        user: userLevels.map((l) => ({ ...l })),
        userLineWidth,
        userLineStyle,
        userDiagonal: { ...userDiagonal },
        userShowDiagonal,
        userLabelSide,
        tabs: tabSnap,
        tabLineWidths: tabWidthSnap,
        tabLineStyles: tabStyleSnap,
      };
      setNewRatioStr(ratioToPercentInput(DEFAULT_NEW_RATIO));
      setNewColor(DEFAULT_NEW_COLOR);
      setNewOpacity(1);
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  /** Write the level list to the active scope via the shared engine (which
   *  routes to the tab override or the user slot). */
  const patchLevels = (next: FibLevel[]) => {
    hook.patch({ ...hook.current, levels: next });
  };

  /** Apply a shared line-style change to the active scope. Color is ignored —
   *  fib uses per-level colors. */
  const patchLineStyle = (cfg: LineStyleConfig) => {
    hook.patch({
      ...hook.current,
      lineWidth: cfg.width,
      lineStyle: cfg.style as LineStyleName,
    });
  };

  const updateRow = (idx: number, patch: Partial<FibLevel>) => {
    if (!editingLevels) return;
    patchLevels(
      editingLevels.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    );
  };

  const deleteRow = (idx: number) => {
    if (!editingLevels) return;
    patchLevels(editingLevels.filter((_, i) => i !== idx));
  };

  const addRow = () => {
    if (!editingLevels) return;
    const pct = Number(newRatioStr);
    if (!Number.isFinite(pct)) return;
    const ratio = percentToRatio(pct);
    if (editingLevels.some((r) => Math.abs(r.ratio - ratio) < 1e-6)) return;
    const next: FibLevel = {
      ratio,
      color: newColor,
      enabled: true,
      opacity: newOpacity,
    };
    patchLevels(
      [...editingLevels, next].sort((a, b) => a.ratio - b.ratio),
    );
    setNewRatioStr(ratioToPercentInput(DEFAULT_NEW_RATIO));
    setNewColor(DEFAULT_NEW_COLOR);
    setNewOpacity(1);
  };

  const closeAndDiscard = () => {
    const r = rollbackRef.current;
    if (r) {
      dispatch(setFibLevels(r.user));
      dispatch(setFibLineWidth(r.userLineWidth));
      dispatch(setFibLineStyle(r.userLineStyle));
      dispatch(setFibDiagonal(r.userDiagonal));
      dispatch(setFibShowDiagonal(r.userShowDiagonal));
      dispatch(setFibLabelSide(r.userLabelSide));
      for (const [tid, lv] of Object.entries(r.tabs)) {
        dispatch(setTabFibLevels({ tabId: tid, fibLevels: lv }));
        dispatch(
          setTabFibLineWidth({
            tabId: tid,
            lineWidth: r.tabLineWidths[tid] ?? null,
          }),
        );
        dispatch(
          setTabFibLineStyle({
            tabId: tid,
            lineStyle: r.tabLineStyles[tid] ?? null,
          }),
        );
      }
    }
    onClose();
  };

  const handleSave = async () => {
    // User-scope levels persist to the server (PUT /api/fib_levels).
    // Workspace + chart levels round-trip via the workspace
    // autosave so they don't need an explicit POST here.
    //
    // Always fire the user-scope POST on Save — cheap, and it
    // covers the case where the user edited User scope earlier
    // in the session without explicitly hitting Save (the local
    // state needs to match the server state to avoid surprises
    // on the next page-load).
    const sortedUser = [...userLevels].sort((a, b) => a.ratio - b.ratio);
    try {
      const result = await dispatch(
        saveFibLevels({
          levels: sortedUser,
          lineWidth: userLineWidth,
          lineStyle: userLineStyle,
          diagonal: userDiagonal,
          showDiagonal: userShowDiagonal,
          labelSide: userLabelSide,
        }),
      );
      if (saveFibLevels.fulfilled.match(result)) {
        rollbackRef.current = null;
      }
    } catch {
      // Network blip — keep the local edits in redux; next
      // page-load's fetchFibLevels will resync.
    }
    onClose();
  };

  // Scope dropdown, current value, inherit/Customize and the ⋯-menu actions
  // (Reset to defaults / Copy from User defaults / Remove workspace override)
  // all come from the shared `hook` now.

  return (
    <SettingsPanel
      open={open}
      title="Fibonacci levels"
      scope={hook.scopeUi}
      actions={hook.actions}
      saving={saving}
      onCancel={closeAndDiscard}
      onSave={handleSave}
      bodyClassName={styles.roomy}
    >
        {/* Diagonal line + labels — GLOBAL (apply to every chart),
            independent of the Workspace/User scope toggle which only
            governs the level list. Shown at the top, above the levels. */}
        <div className={styles.globalSection}>
          <p className={styles.globalLabel}>Diagonal &amp; labels (all charts)</p>
          <div className={styles.lineRow}>
            <span className={styles.lineRowLabel}>Diagonal line</span>
            <label className={styles.inlineCheck}>
              <input
                type="checkbox"
                checked={userShowDiagonal}
                onChange={(e) =>
                  dispatch(setFibShowDiagonal(e.target.checked))
                }
              />
              Show
            </label>
            <button
              type="button"
              className={styles.lineBtn}
              onClick={() => setDiagonalDialogOpen(true)}
              title="Edit the diagonal/trend line colour, width + style"
            >
              <svg
                className={styles.linePreview}
                viewBox="0 0 56 12"
                preserveAspectRatio="none"
                aria-hidden
              >
                <line
                  x1="2"
                  y1="6"
                  x2="54"
                  y2="6"
                  stroke={userDiagonal.color}
                  strokeWidth={userDiagonal.width}
                  strokeDasharray={
                    ({
                      solid: "",
                      dotted: "2 3",
                      dashed: "6 3",
                      largeDashed: "10 4",
                      sparseDotted: "1 5",
                    } as Record<string, string>)[userDiagonal.style] ?? ""
                  }
                  strokeLinecap="round"
                />
              </svg>
              <span>Edit…</span>
            </button>
          </div>
          <div className={styles.lineRow}>
            <span className={styles.lineRowLabel}>Labels</span>
            <div className={styles.segmented} role="radiogroup">
              {(["left", "right"] as const).map((side) => (
                <button
                  key={side}
                  type="button"
                  role="radio"
                  aria-checked={userLabelSide === side}
                  className={`${styles.segment} ${userLabelSide === side ? styles.segmentActive : ""}`}
                  onClick={() => dispatch(setFibLabelSide(side))}
                >
                  {side === "left" ? "Left" : "Right"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Diagonal line editor — colour + width + style. */}
        <LineStyleDialog
          open={diagonalDialogOpen}
          title="Fibonacci diagonal line"
          value={userDiagonal}
          onChange={(cfg) => dispatch(setFibDiagonal(cfg as FibDiagonalConfig))}
          onClose={() => setDiagonalDialogOpen(false)}
        />

        {/* No override at the selected scope → show inherit banner
            with a Customize button instead of the level editor. */}
        {!editingLevels && (
          <div className={styles.inheritBanner}>
            <p className={styles.inheritText}>
              <strong>
                {scope === "workspace"
                  ? activeSymbol
                    ? `This chart (${activeSymbol})`
                    : "This chart"
                  : SCOPE_LABELS[scope]}
              </strong>{" "}
              is inheriting from{" "}
              <strong>{inheritFromLabel}</strong>.
            </p>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={hook.customize}
            >
              Customize this chart
            </button>
          </div>
        )}

      {editingLevels ? (
        <>
        {/* Shared line style — width + stroke pattern applied to
            every level + the trend line. Colors stay per-level
            in the grid below; the popup omits the color picker. */}
        <div className={styles.lineRow}>
          <span className={styles.lineRowLabel}>Line style</span>
          <button
            type="button"
            className={styles.lineBtn}
            onClick={() => setLineStyleDialogOpen(true)}
            title="Edit thickness + style applied to all fib levels"
          >
            <svg
              className={styles.linePreview}
              viewBox="0 0 56 12"
              preserveAspectRatio="none"
              aria-hidden
            >
              <line
                x1="2"
                y1="6"
                x2="54"
                y2="6"
                stroke="#9aa0a6"
                strokeWidth={editingLineWidth}
                strokeDasharray={
                  ({
                    solid: "",
                    dotted: "2 3",
                    dashed: "6 3",
                    largeDashed: "10 4",
                    sparseDotted: "1 5",
                  } as Record<string, string>)[editingLineStyle] ?? ""
                }
                strokeLinecap="round"
              />
            </svg>
            <span>Edit…</span>
          </button>
        </div>

        <div className={styles.grid}>
          {editingLevels.map((row, idx) => (
            <div
              key={idx}
              className={`${styles.cell} ${row.enabled ? "" : styles.cellDisabled}`}
            >
              <input
                type="checkbox"
                checked={row.enabled}
                onChange={(e) =>
                  updateRow(idx, { enabled: e.target.checked })
                }
                aria-label={`Enable ${row.ratio * 100}%`}
                className={styles.check}
              />
              <input
                type="number"
                step="0.1"
                value={ratioToPercentInput(row.ratio)}
                onChange={(e) =>
                  updateRow(idx, {
                    ratio: percentToRatio(Number(e.target.value)),
                  })
                }
                className={styles.numInput}
              />
              <ColorPicker
                value={row.color}
                opacity={row.opacity ?? 1}
                onChange={(color, opacity) =>
                  updateRow(idx, { color, opacity })
                }
                ariaLabel={`Color for ${row.ratio * 100}%`}
              />
              <button
                type="button"
                className={styles.deleteBtn}
                onClick={() => deleteRow(idx)}
                aria-label={`Delete ${row.ratio * 100}%`}
                title="Delete level"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <div className={styles.addRow}>
          <span className={styles.addLabel}>Add level:</span>
          <input
            type="number"
            step="0.1"
            value={newRatioStr}
            onChange={(e) => setNewRatioStr(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addRow();
              }
            }}
            className={styles.numInput}
            placeholder="e.g. 78.6"
          />
          <ColorPicker
            value={newColor}
            opacity={newOpacity}
            onChange={(c, o) => {
              setNewColor(c);
              setNewOpacity(o);
            }}
            ariaLabel="Color for new level"
          />
          <button
            type="button"
            className={styles.addBtn}
            onClick={addRow}
            title="Add level"
          >
            + Add
          </button>
        </div>

        {error ? (
          <div className={styles.error} role="alert">
            {error}
          </div>
        ) : null}

        {/* Shared line-style editor. Color row hidden — fib uses
            per-level colors from the grid above. The popup
            dispatches live like every other line editor; its own
            Cancel restores the line style at popup-open time. */}
        <LineStyleDialog
          open={lineStyleDialogOpen}
          title="Fibonacci line style"
          value={{
            // The picker still needs a color for its style-preview
            // swatches. Use the first enabled level's color so
            // the swatches look representative of the chart.
            color:
              editingLevels.find((l) => l.enabled)?.color ??
              editingLevels[0]?.color ??
              "#787B86",
            width: editingLineWidth,
            style: editingLineStyle,
          }}
          onChange={patchLineStyle}
          onClose={() => setLineStyleDialogOpen(false)}
          hideColor
        />
        </>
      ) : null}
    </SettingsPanel>
  );
}
